// @ts-strict-ignore
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { FormData } from '../../../models/form-data.model';
import { LoginService } from '../../../services/login.service';
import { AnalyticsService } from '../../../services/analytics.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { SessionService } from 'src/app/services/session.service';
import { MatDialog } from '@angular/material/dialog';
import { LocalStorageService } from 'src/app/services/local-storage/local-storage.service';
import { NotificationsService } from 'src/app/services/notifications.service';
import { ZuulError } from 'src/app/models/zuul-error.model';

@Component({
  selector: 'login-form',
  templateUrl: './login-form.component.html',
  styleUrl: '../login.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class LoginFormComponent implements OnInit, OnDestroy {
  @Input() public routeProvided?: string;
  public error: ZuulError;
  public success: any = {};
  public buttonText: string = 'Get Login Code';
  @Output() public readonly togglePage: EventEmitter<string> =
    new EventEmitter<string>();
  private pageClosed = new Subject<void>();
  private email: string;
  public codeScreen: boolean = false;
  public rememberMe: boolean;

  public formData: FormData[] = [
    {
      controlName: 'email',
      errorName: 'email',
      controlType: 'text',
      valueType: 'email',
      placeholder: 'Email',
      autoComplete: 'on',
      validators: { required: true },
    },
  ];

  public codeFormData: FormData[] = [
    {
      controlName: 'code',
      errorName: 'code',
      controlType: 'text',
      valueType:
        navigator.userAgent.toLowerCase().indexOf('firefox') > -1
          ? 'password'
          : 'text',
      placeholder: 'Code',
      autoComplete: 'off',
      validators: { required: true },
    },
  ];
  private dialogRef: MatDialog = inject(MatDialog);

  constructor(
    private readonly loginService: LoginService,
    private readonly analyticsService: AnalyticsService,
    private readonly sessionService: SessionService,
    private readonly localStorageService: LocalStorageService,
    public notificationsService: NotificationsService,
  ) {}

  public ngOnInit(): void {
    this.dialogRef.closeAll();
  }

  public ngOnDestroy(): void {
    this.pageClosed.next();
    this.pageClosed.complete();
  }
  public onRememberMe(event: boolean): void {
    this.rememberMe = event;
  }

  public login(email: any): void {
    this.sessionService.removeUserSession();
    sessionStorage.clear();
    this.notificationsService.setSpinner({ showSpinner: true });
    this.error = null;
    this.email = email.email;
    this.loginService
      .requestCode(email)
      .pipe(takeUntil(this.pageClosed))
      .subscribe({
        next: (res) => {
          // Check toggle and local storage for saved email:
          if (this.rememberMe) {
            this.localStorageService.set('storedEmail', this.email);
          } else {
            this.localStorageService.remove('storedEmail');
          }
          this.notificationsService.setSpinner({ showSpinner: false });
          this.codeScreen = true;
          this.success = {
            message: 'Code sent.',
            timeToWait: res.timeToWait,
            showResendCodeOption: true,
          };
          this.buttonText = '';
        },
        error: (error) => {
          this.error = error.error ?? { message: 'Could not generate token.' };
          this.notificationsService.setSpinner({ showSpinner: false });
        },
      });
  }

  public verifyCodeAndLogin(code: any): void {
    this.notificationsService.setSpinner({ showSpinner: true });
    this.error = null;
    this.success = null;
    const loginRequestPayload = {
      email: this.email,
      loginCode: code.code.replace(/\s/g, ''),
    };

    this.loginService
      .appLogin(loginRequestPayload)
      .pipe(takeUntil(this.pageClosed))
      .subscribe({
        next: (response) => {
          this.sessionService.removeUserSession();
          sessionStorage.clear();
          this.sessionService.setUserSession(response);
          this.loginService.goToProperRoute(this.routeProvided);
          this.notificationsService.setSpinner({ showSpinner: false });
          this.analyticsService.sendEventAnalytics('login', 'successful');
          this.loginService.setDefaultMember();
          this.loginService.setEntityType();
        },
        error: (error) => {
          this.error = error.error ?? { message: 'Could not verify token.' };
          this.notificationsService.setSpinner({ showSpinner: false });
          this.analyticsService.sendEventAnalytics('login', 'failed');
        },
      });
  }

  public getNewCode(message: string): void {
    if (message === 'newCode') {
      const email = { email: this.email };
      this.login(email);
    }
  }

  public showPage(page: string): void {
    this.togglePage.emit(page);
  }
}
